import api from "../../../service/api";
import i18n from "../../../utilities/i18n";

const buildUrl = (practicePk) =>
    `clearing-house/practice/${practicePk}/era-automation-settings/`;

export const getERASettings = (practicePk) => {
    return api.get(buildUrl(practicePk));
};

export const updateERASettings = (practicePk, data) => {
    return api.put(buildUrl(practicePk), data);
};

/* ----- ERA Autopost custom payment rules (clearing-house) ----- */

const customRulesBasePath = (practicePk) =>
    `clearing-house/practice/${practicePk}/era-autopost-custom-payment-rules/`;

const REASON_CODE_PATH = "practice/reason-code/";
const REMARK_CODE_PATH = "practice/remark-code/";
const CLAIM_STATUS_PATH = "practice/claim-status/";
// Backend convention: page_size=0 means return all custom payment rules.
const PAGE_SIZE_ALL = 0;
// Claim-status endpoint does not currently support the "all" convention reliably.
const CLAIM_STATUS_PAGE_SIZE = 1500;

const withQuery = (path, params) =>
    `${path}?${new URLSearchParams(params).toString()}`;
const getWithQuery = (path, params) => api.get(withQuery(path, params));

export const normalizeListResponse = (data) => {
    if (Array.isArray(data)) return data;
    if (data?.results && Array.isArray(data.results)) return data.results;
    return [];
};

export const normalizeRulesListResponse = normalizeListResponse;

const isOk = (status) => status === 200 || status === 201;

export const listCustomPaymentRules = (practicePk, page = 0) => {
    return getWithQuery(customRulesBasePath(practicePk), {
        page,
        page_size: PAGE_SIZE_ALL,
    });
};

export const createCustomPaymentRule = (practicePk, body) => {
    return api.post(customRulesBasePath(practicePk), body);
};

export const patchCustomPaymentRule = (practicePk, ruleId, body) => {
    return api.patch(`${customRulesBasePath(practicePk)}${ruleId}/`, body);
};

export const deleteCustomPaymentRule = (practicePk, ruleId) => {
    return api.destroy(`${customRulesBasePath(practicePk)}${ruleId}/`);
};

export const bulkUpdateDenialPriorities = (practicePk, body) => {
    return api.put(
        `${customRulesBasePath(practicePk)}bulk-update-priority/`,
        body
    );
};

/** Search practice CARC / adjustment reason codes for the custom rule typeaheads. */
export async function searchCarcCodes(practicePk, searchText, pageSize = 20) {
    const res = await getWithQuery(REASON_CODE_PATH, {
        practice_pk: practicePk,
        current_practice_id: practicePk,
        page: 0,
        page_size: pageSize,
        list_type: "auto",
        name_search: "True",
        search: searchText || "",
    });
    return { ok: isOk(res?.status), options: normalizeListResponse(res?.data) };
}

/** Search remittance remark codes for the custom rule typeaheads. */
export async function searchRemarkCodes(practicePk, searchText, pageSize = 20) {
    const res = await getWithQuery(REMARK_CODE_PATH, {
        practice_pk: practicePk,
        current_practice_id: practicePk,
        page: 0,
        page_size: pageSize,
        search: searchText || "",
    });
    return { ok: isOk(res?.status), options: normalizeListResponse(res?.data) };
}

/** Load claim statuses used by denial routing outcome dropdowns. */
export async function fetchClaimStatusesForPractice(
    practicePk,
    pageSize = CLAIM_STATUS_PAGE_SIZE
) {
    const res = await getWithQuery(CLAIM_STATUS_PATH, {
        practice_pk: practicePk,
        page: 0,
        page_size: pageSize,
        list_type: "null",
    });
    return { ok: isOk(res?.status), options: normalizeListResponse(res?.data) };
}

/**
 * Normalize API errors: envelope `{ status_code, message }` vs DRF field errors.
 */
export function formatApiError(data) {
    if (data == null) return i18n.t("errorMessages.commonError");
    if (typeof data === "string") return data;
    if (data.message && typeof data.message === "string" && data.status_code != null) {
        return data.message;
    }
    if (data.message && typeof data.message === "string") return data.message;

    const body = data.data ?? data;
    if (body?.message && body?.status_code != null) return body.message;

    const parts = [];
    const walk = (obj, prefix = "") => {
        if (!obj || typeof obj !== "object") return;
        Object.entries(obj).forEach(([k, v]) => {
            const key = prefix ? `${prefix}.${k}` : k;
            if (Array.isArray(v) && v.every((x) => typeof x === "string")) {
                parts.push(`${key}: ${v.join(" ")}`);
            } else if (typeof v === "string") {
                parts.push(`${key}: ${v}`);
            } else if (v && typeof v === "object" && !Array.isArray(v)) {
                walk(v, key);
            }
        });
    };
    walk(body);
    if (parts.length) return parts.join("; ");
    return i18n.t("errorMessages.commonError");
}
